/**
 * Format checks shared by every form AI fills in (eKYC, hồ sơ nông hộ, thửa
 * đất, mùa vụ, hợp đồng). Each returns an error message, or undefined when the
 * value is acceptable. Empty values are only an error where `required`.
 */

export type Check = (value: string) => string | undefined;

const parseVnDate = (v: string): Date | undefined => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v.trim());
  if (!m) return undefined;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return d.getDate() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 ? d : undefined;
};
const parseIsoDate = (v: string): Date | undefined => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getDate() === Number(m[3]) ? d : undefined;
};
const yearsBetween = (a: Date, b: Date) => (b.getTime() - a.getTime()) / (365.25 * 86_400_000);

export const required =
  (label: string): Check =>
  (v) =>
    v.trim() ? undefined : `Vui lòng nhập ${label}`;

/** Runs checks in order; `required` decides whether empty is an error at all. */
export function validate(value: string, checks: Check[], isRequired = false, label = 'trường này'): string | undefined {
  if (!value.trim()) return isRequired ? `Vui lòng nhập ${label}` : undefined;
  for (const c of checks) {
    const e = c(value);
    if (e) return e;
  }
  return undefined;
}

export const fullName: Check = (v) => {
  if (v.trim().length < 2) return 'Họ tên quá ngắn';
  if (/\d/.test(v)) return 'Họ tên không được chứa chữ số';
  if (!/^[\p{L}\s.'-]+$/u.test(v.trim())) return 'Họ tên chỉ gồm chữ cái';
  return undefined;
};

export const cccd12: Check = (v) => (/^\d{12}$/.test(v.trim()) ? undefined : 'Số CCCD phải có đúng 12 chữ số');

export const phoneVn: Check = (v) => (/^0\d{9}$/.test(v.trim()) ? undefined : 'Số điện thoại phải có 10 chữ số, bắt đầu bằng 0');

export const address: Check = (v) => (v.trim().length >= 8 ? undefined : 'Địa chỉ quá ngắn — ghi rõ ấp/xã, huyện, tỉnh');

export const gender: Check = (v) => (v === 'Nam' || v === 'Nữ' ? undefined : 'Giới tính chỉ là Nam hoặc Nữ');

/** dd/mm/yyyy, a real date. */
export const vnDate: Check = (v) => (parseVnDate(v) ? undefined : 'Ngày phải theo dạng dd/mm/yyyy và có thật');

export const notFutureVn: Check = (v) => {
  const d = parseVnDate(v);
  return d && d.getTime() > Date.now() ? 'Ngày không được ở tương lai' : undefined;
};

/** Date of birth on a CCCD: holder 14–110 years old. */
export const birthDate: Check = (v) => {
  const d = parseVnDate(v);
  if (!d) return /^\d{2}\/\d{2}\/\d{4}$/.test(v.trim()) ? 'Ngày sinh không có thật — kiểm tra lại ngày/tháng' : 'Ngày sinh phải theo dạng dd/mm/yyyy';
  const age = yearsBetween(d, new Date());
  if (age < 14) return 'Ngày sinh không hợp lệ (chưa đủ 14 tuổi)';
  if (age > 110) return 'Ngày sinh không hợp lệ';
  return undefined;
};

/** A CCCD number that contradicts the printed birth year / gender is a misread. */
export function cccdMatchesBirth(id: string, dob: string, sex?: string): string | undefined {
  if (!/^\d{12}$/.test(id)) return undefined;
  const d = parseVnDate(dob);
  if (!d) return undefined;
  const centuryDigit = Number(id[3]);
  const year = d.getFullYear();
  const expectedCentury = 1900 + Math.floor(centuryDigit / 2) * 100;
  if (year - (year % 100) !== expectedCentury || String(year % 100).padStart(2, '0') !== id.slice(4, 6)) {
    return 'Số CCCD không khớp năm sinh — kiểm tra lại số hoặc ngày sinh';
  }
  if (sex && sex !== (centuryDigit % 2 === 0 ? 'Nam' : 'Nữ')) return 'Số CCCD không khớp giới tính';
  return undefined;
}

/** Positive number, optionally capped. */
export const positive =
  (label: string, max?: number): Check =>
  (v) => {
    const n = Number(v.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return `${label} phải là số lớn hơn 0`;
    if (max !== undefined && n > max) return `${label} quá lớn (tối đa ${max.toLocaleString('vi-VN')})`;
    return undefined;
  };

/** yyyy-mm-dd from a date input. */
export const isoDate: Check = (v) => (parseIsoDate(v) ? undefined : 'Ngày không hợp lệ');

export function isoAfter(later: string, earlier: string, message: string): string | undefined {
  const a = parseIsoDate(later);
  const b = parseIsoDate(earlier);
  return a && b && a.getTime() <= b.getTime() ? message : undefined;
}

export function vnAfter(later: string, earlier: string, message: string): string | undefined {
  const a = parseVnDate(later);
  const b = parseVnDate(earlier);
  return a && b && a.getTime() <= b.getTime() ? message : undefined;
}
